<template>
  <b-modal
    ref="modal"
    v-model="isVisible"
    size="lg"
    centered
    @hidden="resetModal"
    hide-footer
    body-class="p-0"
  >
    <template #modal-header="{ close }">
      <div class="modal-header-custom">
        <h5 class="modal-title">
          <i class="las la-file-alt"></i>
          Company Analysis Pack Requirements
        </h5>
        <button type="button" class="close-btn" @click="close()" aria-label="Close">
          <i class="las la-times"></i>
        </button>
      </div>
    </template>

    <div class="analysis-pack-modal-body">
      <div v-if="loading" class="modal-loading">
        <div class="loading-spinner"></div>
        <p>Loading files...</p>
      </div>
      
      <div v-else>
        <!-- Requirements Section -->
        <div class="requirements-section">
          <h6 class="section-title">
            <i class="las la-list-check"></i>
            Required File Types
          </h6>
          <div class="requirements-grid">
            <div 
              v-for="fileType in requiredFileTypes" 
              :key="fileType"
              class="requirement-item"
              :class="{ 
                'met': isRequirementMet(fileType), 
                'missing': !isRequirementMet(fileType),
                'selected': selectedFileType === fileType
              }"
              @click="selectFileType(fileType)"
            >
              <div class="requirement-header">
                <div class="requirement-status">
                  <i :class="isRequirementMet(fileType) ? 'las la-check-circle' : 'las la-times-circle'"></i>
                </div>
                <div class="requirement-label">{{ formatFileType(fileType) }}</div>
              </div>
            </div>
          </div>
        </div>

        <!-- Files List Section -->
        <div v-if="selectedFileType" class="files-section">
          <h6 class="section-title">
            <i class="las la-folder-open"></i>
            {{ formatFileType(selectedFileType) }} Files
          </h6>
          <RequirementFilesList 
            :files="getFilesForType(selectedFileType)"
            :file-type="selectedFileType"
          />
        </div>

        <!-- Progress Section -->
        <div v-if="generating || progressData" class="progress-section">
          <h6 class="section-title">
            <i class="las la-tasks"></i>
            Generation Progress
          </h6>
          <div class="progress-container">
            <div v-if="progressData" class="progress-info">
              <div class="progress-status" :class="progressData.status">
                <i :class="getStatusIcon(progressData.status)"></i>
                <span>{{ progressData.status || 'Processing' }}</span>
              </div>
              <div v-if="progressData.message" class="progress-message">
                {{ progressData.message }}
              </div>
              <div v-if="progressData.progress !== undefined && progressData.progress !== null" class="progress-bar-container">
                <div class="progress-bar">
                  <div 
                    class="progress-bar-fill" 
                    :style="{ width: `${progressData.progress}%` }"
                  ></div>
                </div>
                <div class="progress-percentage">{{ progressData.progress }}%</div>
              </div>
              <div v-if="progressData.error" class="progress-error">
                <i class="las la-exclamation-triangle"></i>
                <span>{{ progressData.error }}</span>
              </div>
            </div>
            <div v-else class="progress-info">
              <div class="progress-status processing">
                <i class="las la-spinner la-spin"></i>
                <span>Initializing...</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Action Buttons -->
        <div class="modal-actions">
          <button 
            class="btn-cancel" 
            @click="closeModal"
            :disabled="generating"
          >
            {{ generating ? 'Close' : 'Cancel' }}
          </button>
          <button 
            class="btn-generate" 
            @click="generateAnalysisPack"
            :disabled="!allRequirementsMet || generating"
          >
            <i v-if="generating" class="las la-spinner la-spin"></i>
            <i v-else class="las la-download"></i>
            {{ generating ? 'Generating...' : 'Generate Analysis Pack' }}
          </button>
        </div>
      </div>
    </div>
  </b-modal>
</template>

<script>
import ChatbotService from "@/services/chatbot.service";
import RequirementFilesList from "./RequirementFilesList.vue";

export default {
  name: 'AnalysisPackModal',
  components: {
    RequirementFilesList
  },
  props: {
    candidateId: {
      type: String,
      required: false,
      default: null
    }
  },
  data() {
    return {
      isVisible: false,
      loading: false,
      currentFiles: [],
      requiredFileTypes: [
        'bank_statement',
        'emirates_id',
        'trade_license',
        'vat_statement',
        'aecb_report'
      ],
      generating: false,
      selectedFileType: null, // Track which file type is selected to show files
      websocket: null,
      progressData: null, // Store WebSocket progress updates
      analysisPackFile: null // Track the created analysis pack file
    }
  },
  watch: {
    isVisible(newVal) {
      if (newVal) {
        this.loadFiles();
        this.connectWebSocket();
      } else {
        this.disconnectWebSocket();
      }
    }
  },
  beforeDestroy() {
    this.disconnectWebSocket();
  },
  computed: {
    allRequirementsMet() {
      return this.requiredFileTypes.every(type => this.isRequirementMet(type));
    }
  },
  methods: {
    async loadFiles() {
      if (!this.candidateId) {
        console.warn('AnalysisPackModal: candidateId is required to load files');
        return;
      }
      
      this.loading = true;
      this.currentFiles = [];
      let page = 1;
      try {
        let filesData = await ChatbotService.getUploads(this.candidateId, { page: page});
        this.currentFiles = [...this.currentFiles, ...filesData.data];
        while (filesData.next !== null) {
          filesData = await ChatbotService.getUploads(this.candidateId, { page: page});
          this.currentFiles = [...this.currentFiles, ...filesData.data];
          page++;
        }
      } catch (err) {
        console.error('Error loading files:', err);
        this.$bvToast.toast('Failed to load files.', {
          title: 'Error',
          variant: 'danger',
          solid: true,
          autoHideDelay: 5000
        });
        this.currentFiles = [];
      } finally {
        this.loading = false;
      }
    },
    
    isRequirementMet(fileType) {
      return this.currentFiles.some(file => {
        const fileTypeValue = file.type || file.document_type;
        // Handle both 'trade_license' and 'trade_licence' spellings
        if (fileType === 'trade_license') {
          return fileTypeValue === 'trade_license' || fileTypeValue === 'trade_licence';
        }
        return fileTypeValue === fileType;
      });
    },
    
    getFilesForType(fileType) {
      return this.currentFiles.filter(file => {
        const fileTypeValue = file.type || file.document_type;
        // Handle both 'trade_license' and 'trade_licence' spellings
        if (fileType === 'trade_license') {
          return fileTypeValue === 'trade_license' || fileTypeValue === 'trade_licence';
        }
        return fileTypeValue === fileType;
      });
    },
    
    selectFileType(fileType) {
      // Toggle selection - if already selected, deselect; otherwise select
      if (this.selectedFileType === fileType) {
        this.selectedFileType = null;
      } else {
        this.selectedFileType = fileType;
      }
    },
    
    async generateAnalysisPack() {
      if (!this.candidateId) {
        this.$bvToast.toast('Candidate ID is required.', {
          title: 'Error',
          variant: 'danger',
          solid: true,
          autoHideDelay: 5000
        });
        return;
      }
      
      if (!this.allRequirementsMet) {
        this.$bvToast.toast('Please ensure all required file types are uploaded.', {
          title: 'Missing Requirements',
          variant: 'warning',
          solid: true,
          autoHideDelay: 5000
        });
        return;
      }
      
      this.generating = true;
      this.progressData = null;
      this.analysisPackFile = null;
      
      try {
        // The API call will trigger the file creation, WebSocket will track progress
        await ChatbotService.generateCompanyAnalysisPack(this.candidateId);
        // Don't wait for response - WebSocket will handle progress updates
      } catch (err) {
        console.error('Error initiating company analysis pack generation:', err);
        const errorMessage = err.response?.data?.message || err.message || 'Failed to initiate analysis pack generation. Please try again.';
        this.$bvToast.toast(errorMessage, {
          title: 'Error',
          variant: 'danger',
          solid: true,
          autoHideDelay: 5000
        });
        this.generating = false;
        this.progressData = null;
      }
    },
    
    connectWebSocket() {
      if (!this.candidateId) {
        return;
      }
      
      try {
        const wsUrl = `${process.env.VUE_APP_WEBSOCKET_URL}/ws/analysis-pack/${this.candidateId}/`;
        
        console.log('Connecting to Analysis Pack WebSocket:', wsUrl);
        
        this.websocket = new WebSocket(wsUrl);
        
        this.websocket.onopen = () => {
          console.log('Analysis Pack WebSocket connected');
        };
        
        this.websocket.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            this.handleWebSocketMessage(data);
          } catch (error) {
            console.error('Error parsing WebSocket message:', error);
          }
        };
        
        this.websocket.onerror = (error) => {
          console.error('Analysis Pack WebSocket error:', error);
        };
        
        this.websocket.onclose = () => {
          console.log('Analysis Pack WebSocket disconnected');
        };
      } catch (error) {
        console.error('Error connecting to Analysis Pack WebSocket:', error);
      }
    },
    
    disconnectWebSocket() {
      if (this.websocket) {
        this.websocket.close();
        this.websocket = null;
      }
    },
    
    handleWebSocketMessage(data) {
      if (!data || typeof data !== 'object') {
        console.log('Invalid WebSocket message data:', data);
        return;
      }
      
      // Handle progress updates
      if (data.type === 'progress') {
        this.progressData = {
          status: data.status,
          message: data.message || '',
          progress: data.progress || 0,
          upload_id: data.upload_id,
          error: data.error
        };
        
        // Handle completion
        if (data.status === 'completed' || data.status === 'success') {
          this.generating = false;
          this.$bvToast.toast('Company Analysis Pack generated successfully.', {
            title: 'Success',
            variant: 'success',
            solid: true,
            autoHideDelay: 5000
          });
          
          // Fetch the created file with type 'analysis_pack'
          this.fetchAnalysisPackFile();
          
          this.$emit('generated');
          
          // Close the modal after a short delay to show the success message
          setTimeout(() => {
            this.closeModal();
          }, 1500);
        }
        
        // Handle errors
        if (data.status === 'failed' || data.status === 'error' || data.error) {
          this.generating = false;
          const errorMessage = data.error || data.message || 'Failed to generate analysis pack.';
          this.$bvToast.toast(errorMessage, {
            title: 'Error',
            variant: 'danger',
            solid: true,
            autoHideDelay: 5000
          });
        }
      }
    },
    
    async fetchAnalysisPackFile() {
      if (!this.candidateId) {
        return;
      }
      
      try {
        // Fetch files and find the analysis_pack file
        const filesData = await ChatbotService.getUploads(this.candidateId);
        const files = Array.isArray(filesData) 
          ? filesData 
          : (filesData.files || filesData.uploads || filesData.data || []);
        
        this.analysisPackFile = files.find(file => {
          const fileType = file.type || file.document_type;
          return fileType === 'analysis_pack';
        });
        
        if (this.analysisPackFile) {
          console.log('Analysis pack file found:', this.analysisPackFile);
          // You can emit this or handle it as needed
          this.$emit('analysis-pack-created', this.analysisPackFile);
        }
      } catch (error) {
        console.error('Error fetching analysis pack file:', error);
      }
    },
    
    getStatusIcon(status) {
      switch (status) {
        case 'completed':
        case 'success':
          return 'las la-check-circle';
        case 'failed':
        case 'error':
          return 'las la-times-circle';
        case 'processing':
        case 'generating':
          return 'las la-spinner la-spin';
        default:
          return 'las la-info-circle';
      }
    },
    
    closeModal() {
      this.isVisible = false;
    },
    
    resetModal() {
      this.currentFiles = [];
      this.loading = false;
      this.generating = false;
      this.isVisible = false;
      this.selectedFileType = null;
      this.progressData = null;
      this.analysisPackFile = null;
    },
    
    show() {
      this.isVisible = true;
    },
    
    getFileIcon(file) {
      const documentType = file.document_type || file.type;
      
      switch (documentType) {
        case 'application/pdf':
        case 'pdf':
          return 'las la-file-pdf';
        case 'application/vnd.ms-excel':
        case 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
        case 'excel':
        case 'xlsx':
        case 'xls':
          return 'las la-file-excel';
        case 'application/msword':
        case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
        case 'word':
        case 'doc':
        case 'docx':
          return 'las la-file-word';
        case 'application/vnd.ms-powerpoint':
        case 'application/vnd.openxmlformats-officedocument.presentationml.presentation':
        case 'powerpoint':
        case 'ppt':
        case 'pptx':
          return 'las la-file-powerpoint';
        case 'text/csv':
        case 'csv':
          return 'las la-file-csv';
        case 'application/json':
        case 'json':
          return 'las la-file-code';
        case 'image/jpeg':
        case 'image/png':
        case 'image/gif':
        case 'image':
          return 'las la-file-image';
        default:
          return 'las la-file-alt';
      }
    },
    
    formatFileName(name) {
      if (!name) return 'Unknown File';
      return name.split('.')[0];
    },
    
    formatFileType(type) {
      switch (type) {
        case 'bank_statement':
          return 'Bank Statement';
        case 'emirates_id':
          return 'Emirates ID';
        case 'trade_license':
          return 'Trade License';
        case 'vat_statement':
          return 'VAT Statement';
        case 'aecb_report':
          return 'AECB Report';
        case 'payer_list':
          return 'Payer List';
        case 'credit_model':
          return 'Credit Model';
        default:
          return type || 'Unknown';
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import '../index.scss';

.modal-header-custom {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 0;
  
  .modal-title {
    margin: 0;
    font-size: 1.25rem;
    font-weight: 600;
    color: #2d3748;
    font-family: $brand-font-medium;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    
    i {
      color: $color-primary;
      font-size: 1.5rem;
    }
  }
  
  .close-btn {
    background: none;
    border: none;
    color: #718096;
    font-size: 1.5rem;
    cursor: pointer;
    padding: 0;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 4px;
    transition: all 0.2s ease;
    
    &:hover {
      background: #f7fafc;
      color: #2d3748;
    }
  }
}

.analysis-pack-modal-body {
  padding: 1.25rem;
  
  .modal-loading {
    text-align: center;
    padding: 3rem 2rem;
    
    .loading-spinner {
      width: 40px;
      height: 40px;
      border: 4px solid #f3f3f3;
      border-top: 4px solid $color-primary;
      border-radius: 50%;
      animation: spin 1s linear infinite;
      margin: 0 auto 1rem;
    }
    
    p {
      color: #718096;
      font-size: 1rem;
      margin: 0;
    }
  }
}

.requirements-section {
  margin-bottom: 1.5rem;
  
  .section-title {
    font-size: 1rem;
    font-weight: 600;
    color: #2d3748;
    margin: 0 0 1rem 0;
    font-family: $brand-font-medium;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    
    i {
      color: $color-primary;
      font-size: 1.1rem;
    }
  }
  
  .requirements-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 0.75rem;
    
    @media (max-width: 576px) {
      grid-template-columns: 1fr;
    }
  }
  
  .requirement-item {
    border-radius: 8px;
    background: #f7fafc;
    border: 1px solid #e2e8f0;
    transition: all 0.2s ease;
    overflow: hidden;
    
    &.met {
      background: #f0fdf4;
      border-color: #86efac;
      
      .requirement-status i {
        color: #16a34a;
      }
    }
    
    &.missing {
      background: #fef2f2;
      border-color: #fecaca;
      
      .requirement-status i {
        color: #dc2626;
      }
    }
    
    &.selected {
      border-color: $color-primary;
      box-shadow: 0 2px 8px rgba($color-primary, 0.2);
      background: rgba($color-primary, 0.05);
    }
    
    .requirement-header {
      display: flex;
      align-items: center;
      gap: 0.75rem;
      padding: 0.75rem;
      cursor: pointer;
      transition: background 0.2s ease;
      
      &:hover {
        background: rgba(0, 0, 0, 0.02);
      }
    }
    
    .requirement-status {
      i {
        font-size: 1.25rem;
      }
    }
    
    .requirement-label {
      flex: 1;
      font-size: 0.9rem;
      font-weight: 500;
      color: #2d3748;
      font-family: $brand-font-medium;
    }
  }
}

.files-section {
  margin-top: 1.5rem;
  margin-bottom: 1rem;
  
  .section-title {
    font-size: 1rem;
    font-weight: 600;
    color: #2d3748;
    margin: 0 0 1rem 0;
    font-family: $brand-font-medium;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    
    i {
      color: $color-primary;
      font-size: 1.1rem;
    }
  }
}

.progress-section {
  margin-top: 1.5rem;
  margin-bottom: 1rem;
  padding: 1rem;
  background: #f7fafc;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  
  .section-title {
    font-size: 1rem;
    font-weight: 600;
    color: #2d3748;
    margin: 0 0 1rem 0;
    font-family: $brand-font-medium;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    
    i {
      color: $color-primary;
      font-size: 1.1rem;
    }
  }
  
  .progress-container {
    .progress-info {
      display: flex;
      flex-direction: column;
      gap: 0.75rem;
    }
    
    .progress-status {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      font-weight: 600;
      font-size: 0.95rem;
      
      i {
        font-size: 1.1rem;
      }
      
      &.processing, &.generating {
        color: #3b82f6;
        
        i {
          color: #3b82f6;
        }
      }
      
      &.completed, &.success {
        color: #16a34a;
        
        i {
          color: #16a34a;
        }
      }
      
      &.failed, &.error {
        color: #dc2626;
        
        i {
          color: #dc2626;
        }
      }
    }
    
    .progress-message {
      color: #4a5568;
      font-size: 0.9rem;
      line-height: 1.5;
    }
    
    .progress-bar-container {
      display: flex;
      align-items: center;
      gap: 1rem;
      
      .progress-bar {
        flex: 1;
        height: 8px;
        background: #e2e8f0;
        border-radius: 4px;
        overflow: hidden;
        
        .progress-bar-fill {
          height: 100%;
          background: linear-gradient(90deg, $color-primary, lighten($color-primary, 10%));
          border-radius: 4px;
          transition: width 0.3s ease;
        }
      }
      
      .progress-percentage {
        font-size: 0.85rem;
        font-weight: 600;
        color: #4a5568;
        min-width: 45px;
        text-align: right;
      }
    }
    
    .progress-error {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      padding: 0.75rem;
      background: #fef2f2;
      border: 1px solid #fecaca;
      border-radius: 6px;
      color: #dc2626;
      font-size: 0.9rem;
      
      i {
        font-size: 1.1rem;
      }
    }
  }
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  padding-top: 1rem;
  border-top: 1px solid #e2e8f0;
  margin-top: 1rem;
  
  .btn-cancel {
    padding: 0.75rem 1.5rem;
    background: #f7fafc;
    color: #4a5568;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    font-size: 0.9rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    font-family: $brand-font-medium;
    
    &:hover {
      background: #edf2f7;
      border-color: #cbd5e0;
    }
  }
  
  .btn-generate {
    padding: 0.75rem 1.5rem;
    background: $color-primary;
    color: $color-secondary;
    border: none;
    border-radius: 8px;
    font-size: 0.9rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-family: $brand-font-medium;
    
    &:hover:not(:disabled) {
      background: darken($color-primary, 10%);
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba($color-primary, 0.3);
    }
    
    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
    
    i {
      font-size: 1rem;
    }
  }
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
</style>

